import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';

import { AsyncPipe, CommonModule } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, Observable } from 'rxjs';
import { DealerGroup, DealerGroupMember, DealerGroupsState } from '../../interfaces/dealer-group.interface';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { LeadSellerPreferencesService } from '../../services/lead-seller-preferences/lead-seller-preferences.service';
import { map, takeUntil } from 'rxjs/operators';
import {
  selectGroup,
  selectGroupIsSelected,
  selectGroupMembers,
  selectGroupMemberSelectedCount
} from '../../services/lead-seller-group/reducer';
import { SearchFilterSectionComponent } from '../search-filter/search-filter-section/search-filter-section.component';
import { TrackDirective } from 'remarketing-ui-components-lib';
import { SaComponent } from 'smartauction-frontend-micro-library';

@Component({
  selector: 'app-dealer-group-selection-section',
  templateUrl: './dealer-group-selection-section.component.html',
  styleUrl: './dealer-group-selection-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SearchFilterSectionComponent, CommonModule, TrackDirective],
  standalone: true
})
export class DealerGroupSelectionSectionComponent extends SaComponent implements OnInit {
  readonly selectGroup = output<string>();
  readonly selectMember = output<string>();

  readonly state$ = input<Observable<DealerGroupsState>>();
  readonly groupUuid$ = input<string>();
  readonly dealershipGroupCount$ = input<number>();
  readonly leadSellerPreferencePopOver$ = input<boolean>();

  private readonly translocoService = inject(TranslocoService);
  private readonly leadSellerGroupService = inject(LeadSellerGroupService);
  private readonly leadSellerPreferencesService = inject(LeadSellerPreferencesService);

  group$: Observable<DealerGroup>;
  members$: Observable<DealerGroupMember[]>;
  selectedMemberCount$: Observable<number>;
  isGroupSelected$: Observable<boolean>;
  searchTerm$: Observable<string>;
  filteredMembers$: Observable<DealerGroupMember[]>;
  open = false;
  tagName = 'lead-seller';
  translations = {
    labelAll: '',
    loading: ''
  };

  constructor() {
    super();
  }

  ngOnInit() {
    this.getTranslations();
    this.searchTerm$ = this.leadSellerGroupService.searchTerm$;
    this.group$ = this.state$().pipe(map(selectGroup(this.groupUuid$())));
    this.members$ = this.state$().pipe(map(selectGroupMembers(this.groupUuid$())));
    this.filteredMembers$ = combineLatest(this.members$, this.searchTerm$).pipe(
      map(([members, searchTerm]) => members.filter(member => member.oeName.toLowerCase().includes(searchTerm)))
    );
    combineLatest(this.filteredMembers$, this.searchTerm$)
    .pipe(takeUntil(this.destroyed$))
    .subscribe(([filteredMembers, searchTerm]) => {
      if (searchTerm !== '') {
        this.open = filteredMembers.length > 0;
      }
    });
    this.selectedMemberCount$ = this.state$().pipe(map(selectGroupMemberSelectedCount(this.groupUuid$())));
    this.isGroupSelected$ = this.state$().pipe(map(selectGroupIsSelected(this.groupUuid$())));
  }

  toggleGroup() {
    this.selectGroup.emit(this.groupUuid$());
  }

  toggleMember(uuid: string) {
    this.selectMember.emit(uuid);
  }

  toggleAllMembersInGroup() {
    if (this.leadSellerPreferencePopOver$()) {
      this.leadSellerPreferencesService.toggleAll();
    } else {
      this.leadSellerGroupService.toggleAll();
    }
  }

  get isSellerAssociatedToMultipleLeadSellerGroup() {
    return this.dealershipGroupCount$() && this.dealershipGroupCount$() > 1;
  }

  get isSellerAssociatedToSingleLeadSellerGroup() {
    return this.dealershipGroupCount$() && this.dealershipGroupCount$() === 1;
  }

  getGroupCheckboxId(uuid: string, isMultiple: boolean): string {
    return isMultiple ? `dealer-group-popover-select-all-${uuid}` : `dealer-group-popover-select-all-single-${uuid}`;
  }

  getMemberCheckboxId(oeUuid: string, isMultiple: boolean): string {
    return isMultiple ? `dealer-group-member-select-${oeUuid}` : `dealer-group-member-select-single-${oeUuid}`;
  }

  getGroupTestId(isMultiple: boolean): string {
    return isMultiple
      ? 'app-dealer-group-section-e-select-all-checkbox'
      : 'app-dealer-group-section-e-select-all-single-checkbox';
  }

  getGroupDescriptionId(uuid: string, isMultiple: boolean): string {
    return `group-description-${uuid}-${isMultiple ? 'multiple' : 'single'}`;
  }

  getMemberDescriptionId(oeUuid: string): string {
    return `member-description-${oeUuid}`;
  }

  getGroupTrackingConfig(groupName: string) {
    return { elem: 'checkbox', trigger: 'filter', name: `All ${groupName}` };
  }

  getMemberTrackingConfig(memberName: string) {
    return { elem: 'checkbox', trigger: 'filter', name: memberName };
  }

  handleGroupToggle(isMultiple: boolean): void {
    if (isMultiple) {
      this.toggleGroup();
    } else {
      this.toggleAllMembersInGroup();
    }
  }

  getTranslations(): void {
    const keyParams = {
      'sellModule.manageInventory.dealerGroupSelection': {},
      common: {}
    };

    this.translocoService
    .selectTranslateObject(keyParams)
    .pipe(takeUntil(this.destroyed$))
    .subscribe(translation => {
      this.translations.labelAll = translation[0].labelAll;
    });
  }
}
